import React from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHashtag, 
  faCalendarAlt, 
  faTachometerAlt, 
  faCheckCircle,
  faArrowTrendUp, 
  faArrowTrendDown, 
  faClock,
  faStopwatch
} from '@fortawesome/free-solid-svg-icons';

const formatDuration = minutes => {
  if (!minutes || minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const MetricsCards = ({ data, isLoading = false }) => {
  const totalHoy = data?.iTotalHoy ?? 0;
  const totalAyer = data?.iTotalAyer ?? 0;
  const pendientes = data?.iPendientes ?? 0;
  const enProgreso = data?.iEnProgreso ?? 0;
  const resueltosHoy = data?.iResueltosHoy ?? 0;
  const totalHistorico = data?.iTotalHistorico ?? 0;
  const totalResueltos = data?.iTotalResueltos ?? 0;
  const tiempoRespuestaProm = data?.iTiempoRespuestaPromedio ?? data?.iTiempoRespuestaPromedioMinutos ?? 0;
  const tiempoRespuestaHoy = data?.iTiempoRespuestaPromedioHoy ?? 0;
  const tiempoCierreProm = data?.iTiempoCierrePromedio ?? data?.iTiempoResolucionPromedioMinutos ?? 0;
  const tiempoCierreHoy = data?.iTiempoCierrePromedioHoy ?? 0;

  // Cálculo de tendencia vs ayer
  let trendSubtext = 'Registrados hoy';
  let trendIcon = null;
  let trendColor = '#64748b';

  if (totalAyer > 0) {
    const diffPct = Math.round(((totalHoy - totalAyer) / totalAyer) * 100);
    trendSubtext = `${diffPct >= 0 ? '+' : ''}${diffPct}% vs ayer`;
    trendIcon = diffPct >= 0 ? faArrowTrendUp : faArrowTrendDown;
    trendColor = diffPct >= 0 ? '#10b981' : '#ef4444';
  } else if (totalHoy > 0) {
    trendSubtext = '+100% vs ayer';
    trendIcon = faArrowTrendUp;
    trendColor = '#10b981';
  }

  const cards = [
    {
      label: 'TOTALES HOY',
      value: totalHoy.toLocaleString(),
      icon: faHashtag,
      badgeClass: 'green',
      topBorderClass: 'accent-top-green',
      subtext: totalHistorico > 0 ? `${trendSubtext} • ${totalHistorico.toLocaleString()} histórico` : trendSubtext,
      subtextTrendIcon: trendIcon,
      subtextColor: trendColor
    },
    {
      label: 'PENDIENTES',
      value: pendientes.toLocaleString(),
      icon: faCalendarAlt,
      badgeClass: 'red',
      topBorderClass: 'accent-top-red',
      subtext: pendientes > 0 ? 'Requiere atención inmediata' : 'Al día, sin pendientes',
      subtextTrendIcon: null,
      subtextColor: pendientes > 0 ? '#ef4444' : '#10b981'
    },
    {
      label: 'EN PROGRESO',
      value: enProgreso.toLocaleString(),
      icon: faTachometerAlt,
      badgeClass: 'blue',
      topBorderClass: 'accent-top-blue',
      subtext: enProgreso > 0 ? 'En atención activa' : 'Sin tickets en atención',
      subtextTrendIcon: null,
      subtextColor: '#0284c7'
    },
    {
      label: 'RESUELTOS',
      value: resueltosHoy.toLocaleString(),
      icon: faCheckCircle,
      badgeClass: 'green',
      topBorderClass: 'accent-top-green',
      subtext: totalResueltos > 0 ? `${totalResueltos.toLocaleString()} acumulados en total` : 'Cerrados hoy',
      subtextTrendIcon: null,
      subtextColor: '#059669'
    }
  ];

  const timeCards = [
    {
      label: 'TIEMPO 1RA RESPUESTA (PROM)',
      value: formatDuration(tiempoRespuestaProm),
      icon: faStopwatch,
      subtext: tiempoRespuestaHoy > 0 ? `Hoy: ${formatDuration(tiempoRespuestaHoy)} prom.` : 'Promedio de primera respuesta',
      accentClass: 'accent-purple',
      iconColor: '#6366f1'
    },
    {
      label: 'TIEMPO RESOLUCIÓN / CIERRE (PROM)',
      value: formatDuration(tiempoCierreProm),
      icon: faClock,
      subtext: tiempoCierreHoy > 0 ? `Hoy: ${formatDuration(tiempoCierreHoy)} prom.` : 'Desde 1ra respuesta hasta cierre',
      accentClass: 'accent-cyan',
      iconColor: '#0284c7'
    }
  ];

  return (
    <>
      {/* 4 Métricas Principales en cuadrícula: 2x2 en móvil (xs={6}) / 4 columnas en desktop (xl={3}) */}
      <Row className="g-2 g-sm-3 mb-3">
        {cards.map((m, idx) => (
          <Col key={idx} xs={6} xl={3}>
            <div className={`se-metric-card ${m.topBorderClass} h-100 d-flex flex-column justify-content-between`}>
              <div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="se-metric-label">{m.label}</span>
                  <div className={`se-metric-icon-badge ${m.badgeClass}`}>
                    <FontAwesomeIcon icon={m.icon} />
                  </div>
                </div>

                <div className="se-metric-value">
                  {isLoading ? (
                    <Spinner animation="border" size="sm" variant="secondary" />
                  ) : (
                    m.value
                  )}
                </div>
              </div>

              <div className="se-metric-subtext mt-1" style={{ color: m.subtextColor }}>
                {m.subtextTrendIcon && (
                  <FontAwesomeIcon icon={m.subtextTrendIcon} className="me-1" />
                )}
                {m.subtext}
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* 2 Métricas de Tiempo: 1 columna en móvil (xs={12}) / 2 columnas en desktop (md={6}) */}
      <Row className="g-2 g-sm-3 mb-4">
        {timeCards.map((m, idx) => (
          <Col key={idx} xs={12} md={6}>
            <div className={`se-sla-card ${m.accentClass} h-100`}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="se-metric-label">{m.label}</span>
                <FontAwesomeIcon icon={m.icon} style={{ color: m.iconColor, fontSize: '1.05rem' }} />
              </div>

              <div className="se-metric-value mb-1">
                {isLoading ? (
                  <Spinner animation="border" size="sm" variant="secondary" />
                ) : (
                  m.value
                )}
              </div>

              <div className="se-metric-subtext text-muted" style={{ fontSize: '0.78rem' }}>
                {m.subtext}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );
};

export default MetricsCards;
